import React, { useCallback, useEffect, useRef, useState } from 'react'
import EditIcon from '@mui/icons-material/Edit'

interface InlineEditTextAreaComponentProps {
  defaultValue?: string
  isEditable?: boolean
  label: string
  placeholder?: string
  validate?: (text: string) => boolean
  onSave: (newValue: string) => void // Notify parent on save
  className?: string
  data_testid?: string
}

const InlineEditTextAreaComponent: React.FC<InlineEditTextAreaComponentProps> = ({
  defaultValue = '',
  isEditable = false,
  label,
  placeholder,
  validate,
  onSave,
  className = '',
  data_testid,
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue)
  const [isEditing, setIsEditing] = useState(false)
  const [isError, setIsError] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const saveButtonWrapperRef = useRef<HTMLButtonElement | null>(null)
  // Triggered when canceling edits
  const handleCancel = useCallback(() => {
    setInternalValue(defaultValue)
    setIsEditing(false)
    setIsError(false)
  }, [defaultValue])
  // Adjust height based on content
  const adjustHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto' // Reset height
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px` // Adjust to fit content
    }
  }

  // HOOKS
  useEffect(() => {
    // Adjust height on mount and when internalValue changes
    adjustHeight()
  }, [internalValue])
  // Detect outside clicks to cancel editing
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click target is outside of both the textarea and save button
      if (
        isEditing &&
        textareaRef.current &&
        !textareaRef.current.contains(event.target as Node) &&
        saveButtonWrapperRef.current &&
        !saveButtonWrapperRef.current.contains(event.target as Node)
      ) {
        handleCancel() // Discard changes
      }
    }

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isEditing, handleCancel])

  // HANDLERS
  // Triggered when enabling edit
  const handleEdit = () => {
    if (isEditable) setIsEditing(true) // Enter edit mode
  }

  // Handle user input
  const handleInputChange = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const newValue = e.currentTarget.value
    setInternalValue(newValue)
    if (validate) {
      setIsError(!validate(newValue))
    }
  }

  // Triggered when saving edits
  const handleSave = () => {
    // Don't save if there's a validation error
    if (isError) {
      return
    }

    const trimmedValue = internalValue.trim()

    // If value was cleared, cancel the edit
    if (!trimmedValue) {
      handleCancel()
      return
    }

    // Only save if value has actually changed
    if (trimmedValue !== defaultValue) {
      onSave(trimmedValue)
    }

    setIsEditing(false) // Exit edit mode if no validation error
  }

  // Handle blur
  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    // Only handle blur if the new focus is not the edit button
    if (isEditing && !e.relatedTarget?.closest('button[data-testid="edit-button-testid"]')) {
      handleSave()
    }
  }
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  return (
    <div className='w-full'>
      <div className='flex items-center gap-2'>
        <span className='text-xs font-medium text-gray-700'>{label}</span>
        {isEditable && !isEditing && (
          <button
            onClick={handleEdit}
            data-testid={`${data_testid}-edit-button-testid`}
            className='text-gray-400 hover:text-gray-600 transition-colors p-1 -m-1'
            aria-label='Edit description'
          >
            <span className='w-4 h-4 flex items-center justify-center'>
              <EditIcon fontSize='small' />
            </span>
          </button>
        )}
      </div>

      {isEditing ? (
        <div className='w-full'>
          <textarea
            data-testid={`${data_testid}-textarea-testid`}
            ref={textareaRef}
            value={internalValue}
            placeholder={placeholder}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={`w-full rounded border ${
              isError
                ? 'border-red-500 bg-red-50 focus:ring-1 focus:ring-red-500/20'
                : 'border-gray-300 focus:border-gray-300 focus:ring-1 focus:ring-gray-300/20'
            } outline-none px-3 py-2 text-sm text-gray-900 leading-relaxed ${className}`}
            style={{ minHeight: '80px' }}
            autoFocus
          />
        </div>
      ) : (
        <div
          onClick={isEditable ? handleEdit : undefined}
          className={`w-full min-h-[60px] p-2 -ml-1 rounded ${
            isEditable ? 'hover:bg-gray-50 cursor-text' : ''
          } text-sm text-gray-600 whitespace-pre-wrap break-words leading-relaxed`}
          data-testid={`${data_testid}-span-testid`}
        >
          {internalValue || <span className='text-gray-400'>{placeholder || 'No description'}</span>}
        </div>
      )}
    </div>
  )
}

export default InlineEditTextAreaComponent
